import _ from 'lodash';

import { axiosAPI as axios, withLoading } from 'utils';
import {
  SHOW_FEEDBACK,
  CLOSE_FEEDBACK,
  SHOW_STATS,
  SET_CURRENT_ACTIVITY,
  OPEN_ACTIVITY_HELP,
  CLOSE_ACTIVITY_HELP,
  OPEN_TEAM_ACTIVITY_HELP,
  CLOSE_TEAM_ACTIVITY_HELP,
  GAME_INITIALIZED,
  RESTART_ACTIVITY,
  SHOW_GAME_PARENT_ADVICE,
  SHOW_GAME_MULTIPLAYER_ADVICE,
  ADD_IDLE_TIME,
  CHANGE_PLAYER_TURN,
  INCREMENT_PLAYER_POINTS,
  SET_PLAYER_POWER_LEVEL,
} from 'actions/types';
import API from 'api';
import isMobileAppRuntime from 'utils/isMobileAppRuntime';
import { isOnline } from 'mobile-modules/offline';
import { setOfflineDiagnosticActivityContext } from 'mobile-modules/offline/assets';
import { getCachedPlayBootstrap } from 'mobile-modules/offline/cache';

const getActivityTemplate = (activity, activityTemplates) => {
  return _.find(activityTemplates, { _id: activity.templateId });
};

const filter = 'game';

export const setCurrentActivity = (
  activity,
  activityTemplates,
  options = {}
) => (dispatch) =>
  withLoading(dispatch, async () => {
    // if null passed
    if (!activity) {
      setOfflineDiagnosticActivityContext(null);
      return dispatch({
        filter,
        type: SET_CURRENT_ACTIVITY,
        payload: null,
      });
    }

    // if activity object passed
    if (typeof activity === 'object') {
      const { 
        activity: _activity, // extract activity value from activity object
        template: _template, // extract template value from activity object
      } = activity;

      // if template is set, use it, otherwise extract activity template
      // using activity
      const { multiplayer } = _template ?? getActivityTemplate(_activity, activityTemplates);
      const multiplayerModeEnabled = multiplayer && options.players.length > 1;
      setOfflineDiagnosticActivityContext({
        activityId: _activity?._id || activity?._id || '',
        template: _template?.name || _template?._id || _activity?.templateId || '',
        screen: 'activity',
      });

      const multiplayerMixin = multiplayerModeEnabled ? 
        prepareMultiplayerState(options.players) :
        {};

      return dispatch({
        filter,
        type: SET_CURRENT_ACTIVITY,
        payload: Object.assign({ 
          currentActivity: activity, 
          multiplayerModeEnabled,
          ...options,
        }, multiplayerMixin),
      });
    }

    // if activity ID passed
    if (typeof activity === 'string') {
      const payload = await getActivityData(activity, activityTemplates);
      const { template: { multiplayer } } = payload;
      const multiplayerModeEnabled = multiplayer && options.players.length > 1;
      setOfflineDiagnosticActivityContext({
        activityId: activity,
        template: payload?.template?.name || payload?.template?._id || '',
        screen: 'activity',
      });

      const multiplayerMixin = multiplayerModeEnabled ? 
        prepareMultiplayerState(options.players) :
        {};

      return dispatch({
        filter,
        type: SET_CURRENT_ACTIVITY,
        payload: Object.assign({ 
          currentActivity: payload, 
          multiplayerModeEnabled,
          ...options 
        }, multiplayerMixin),
      });
    }
  });

const findOfflineActivity = async (id) => {
  try {
    const bootstrap = await getCachedPlayBootstrap();
    const activities = bootstrap?.mobileBootstrap?.solutionMenu?.activities || [];
    return activities.find((activity) => String(activity?._id || '') === String(id || '')) || null;
  } catch (error) {
    console.warn('[mobile-offline][activity-metadata-cache-read-failed]', {
      activityId: id,
      error: error?.message || String(error),
    });
    return null;
  }
};

export const getActivityData = async (id, activityTemplates, activeActivity = null) => {
  const result = {};
  const mobileOffline = isMobileAppRuntime() && !isOnline();

  try {
    let activity = activeActivity || null;

    if (!activity && mobileOffline) {
      activity = await findOfflineActivity(id);
      if (!activity) {
        console.error('[mobile-offline][activity-metadata-missing]', { activityId: id });
        window.dispatchEvent(new CustomEvent('flc:offline-content-missing', {
          detail: { activityId: id, type: 'activity-metadata' },
        }));
        return null;
      }
    }

    if (!activity) {
      const response = await axios.get(`api/flink-play/activities/${id}`);
      activity = response.data;
    }

    result.activity = activity;
    result.template = getActivityTemplate(activity, activityTemplates);
    if (!result.template) {
      console.error('[mobile-offline][activity-template-missing]', {
        activityId: id,
        templateId: activity?.templateId || null,
      });
      return null;
    }

    const data = await API.activity.getActivityData(id, result.template);
    if (!data) return null;

    result.data = data;
    return result;
  } catch (err) {
    console.log(err);

    // If connectivity disappeared between menu selection and Activity loading,
    // retry completely from the already cached mobile bootstrap + Activity data.
    if (isMobileAppRuntime()) {
      try {
        const cachedActivity = activeActivity || await findOfflineActivity(id);
        if (cachedActivity) {
          const template = getActivityTemplate(cachedActivity, activityTemplates);
          if (template) {
            const data = await API.activity.getActivityData(id, template);
            if (data) return { activity: cachedActivity, template, data };
          }
        }
      } catch (_) {}
    }

    return null;
  }
};

export const prepareMultiplayerState = (players) => {
  const pointEntries = players.map(({ _id }) => [_id, 0]);
  const powerLevelEntries = players.map(({ _id }) => [_id, 1]);

  return {
    turningPlayerId: _.first(players)._id,
    powerLevels: Object.fromEntries(powerLevelEntries),
    points: Object.fromEntries(pointEntries),
  };
};

export const openActivityHelp = () => ({
  filter,
  type: OPEN_ACTIVITY_HELP,
});

export const closeActivityHelp = () => ({
  filter,
  type: CLOSE_ACTIVITY_HELP,
});

export const openActivityTeamHelp = () => ({
  filter,
  type: OPEN_TEAM_ACTIVITY_HELP,
});

export const closeActivityTeamHelp = () => ({
  filter,
  type: CLOSE_TEAM_ACTIVITY_HELP,
});

export const restartActivity = () => ({
  filter,
  type: RESTART_ACTIVITY,
});

export const setGameInitialized = () => ({
  filter,
  type: GAME_INITIALIZED,
});

export const showStats = (payload) => ({
  filter,
  type: SHOW_STATS,
  payload,
});

export const showGameParentAdvice = (payload) => ({
  filter,
  type: SHOW_GAME_PARENT_ADVICE,
  payload,
});

export const showGameMultiplayerAdvice = (payload) => ({
  filter,
  type: SHOW_GAME_MULTIPLAYER_ADVICE,
  payload,
});

export const showFeedback = (payload) => ({
  filter,
  type: SHOW_FEEDBACK,
  payload,
});

export const closeFeedback = () => ({
  filter,
  type: CLOSE_FEEDBACK,
});

export const addIdleTime = (payload) => ({
  filter,
  type: ADD_IDLE_TIME,
  payload,
});

export const changePlayerTurn = () => {
  return {
    filter,
    type: CHANGE_PLAYER_TURN,
  }
};

export const incrementPlayerPoints = (numGuesses) => {
  return {
    filter,
    type: INCREMENT_PLAYER_POINTS,
    payload: { numGuesses },
  }
};

export const setPlayerPowerLevel = (playerId, powerLevel) => {
  return {
    filter,
    type: SET_PLAYER_POWER_LEVEL,
    payload: {
      id: playerId,
      powerLevel: powerLevel,
    },
  }
};
